import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import {
    isValidDate,
    slugify,
    toDate,
} from '@superbet-group/web.lib.utilities';
import {
    createSimpleGetter,
    createSimpleMutator,
} from '@superbet-group/web.lib.vue-utilities';
import { isEmpty as _isEmpty } from 'lodash';
import {
    Dispatch,
    ActionContext,
    Commit,
} from 'vuex';

import { Link } from '@app/router/constants/Link';
import { IState } from '@app/store';
import {
    INode,
    getBreadcrumbIds,
} from '@app/utils/sidebar';
import OfferEvent from '@core/models/offer/OfferEvent';
import {
    getTimeFilterFromSlug,
    getTimeSpanForTimeFilter,
} from '@shared/timeFilter/helpers';
import StoreUtil from '@store/StoreUtil';
import { RelativeRootGetters } from '@store/types';

import { PredefinedItems } from './enums';
import * as RawFunctions from './rawFunctions';
import { SelectedTreeItem } from './types';
import { SPORTS_HOME_ID } from '../constants';
import { TimeFilterSlug } from '../../shared/timeFilter/utils';
import { RouteName } from '@app/router/constants/RouteName';
import { TimeSpan } from '@shared/timeFilter/types';
import BookieDate from '@core/utils/date/BookieDate';
import { getSportMenuData } from '@src/app/layout/helpers/SportOfferAnalytics';

const ALL_ID = -1000;

const SET_SELECTED_TREE_ITEMS = 'SET_SELECTED_TREE_ITEMS';
const SET_SELECTED_FEATURED_ITEM = 'SET_SELECTED_FEATURED_ITEM';
const CLOSE_TIME_FILTER = 'CLOSE_TIME_FILTER';
const SET_IS_LOADING = 'SET_IS_LOADING';

type FeaturedItem = { ids: SelectedTreeItem[]; link: string };

export interface ISportsState {
    selectedTreeItems: SelectedTreeItem[];
    selectedFeaturedItem: FeaturedItem | null;
    isLoading: boolean;
}

const store = {
    namespaced: true,

    state: {
        selectedTreeItems: [],
        selectedFeaturedItem: null,
        isLoading: true,
    },

    mutations: {
        [SET_SELECTED_TREE_ITEMS]: (state: ISportsState, selectedItems: SelectedTreeItem[]) => {
            state.selectedFeaturedItem = null;
            state.selectedTreeItems = selectedItems;
        },
        [SET_SELECTED_FEATURED_ITEM]: (state: ISportsState, selectedItem: FeaturedItem | null) => {
            state.selectedFeaturedItem = selectedItem;
            state.selectedTreeItems = [];
        },
        [SET_IS_LOADING]: createSimpleMutator<ISportsState, keyof ISportsState>('isLoading'),
    },

    getters: {
        /**
         * This getter takes treeBreadcrumbsIds and returns SelectedTreeItem object
         * with type (sport, tournament, category) and id so we can.  Events are going
         * to be filtered only by one type, the rightmost in breadcrumbs
         * P.S. includes selected tree item + selectedFeaturedItem
         */
        selectedItem: (state: ISportsState, getters: any, rootState: IState, rootGetters: RelativeRootGetters) => {
            return RawFunctions.selectedItem(rootState.ui.sportOffer.sports, rootState, rootGetters);
        },

        /**
         * Maps URL to breadcrumb of ids.
         *
         * This getter takes slugs and maps slugs to ids (from tree for some date, not for struct!).
         * If someone comes from outside with offer/football/anglia method first finds id of football
         * and id of anglia.
         *
         * Example: if path is offer/football/anglia breadcrumb is
         * football > anglia and getter returns [5, 42]
         * // includes featured items
         */
        treeBreadcrumbsIds(state: ISportsState, getters: any, rootState: IState, rootGetters: RelativeRootGetters) {
            const isOnOfferEventPage = rootGetters['navigation/isOnOfferEventPage'];

            if (isOnOfferEventPage) {
                const event = rootGetters['ui/sportOffer/expandedEventData'];
                const breadcrumbIds = event ? getBreadcrumbIds(event, getters.tree, rootState) : [];
                return _isEmpty(breadcrumbIds) ? [ALL_ID] : breadcrumbIds;
            }

            const selectedFeaturedItem = getters.selectedFeaturedItem;
            if (selectedFeaturedItem) {
                return [];
            }
            const breadcrumbIds = getters._selectedFilter;
            return _isEmpty(breadcrumbIds) ? [ALL_ID] : breadcrumbIds;
        },

        _selectedFilter(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            return RawFunctions.breadcrumbsAsIds(state, rootState, rootGetters);
        },

        currentSportId(state: ISportsState, getters: any) {
            return getters._selectedFilter.length > 0
                ? getters._selectedFilter[0]
                : null;
        },

        /**
         * This getter creates and updates sports sidebar tree from events filtered by some date
         * + previously selected breadcrumbs if date doesn't contain items in breadcrumbs or only some items
         */
        tree(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            return RawFunctions.tree(rootState.ui.sportOffer.sports, rootState, rootGetters);
        },

        /**
         * Uses date from dateTimeFilter and returns all events with that date
         */
        eventsFilteredByDate(state: ISportsState, getters: any, rootState: IState, rootGetters: any): OfferEvent[] {
            return RawFunctions.eventsFilteredByDate(state, rootState, rootGetters);
        },

        /**
         * This getter returns events for rendering, events are filtered by date from dateTimeFilter
         * and all the other filters (sport, competition, etc.)
         */
        filteredEvents(state: ISportsState, getters: any, rootState: IState, rootGetters: any): OfferEvent[] {
            return RawFunctions.filteredEvents(state, rootState, rootGetters);
        },

        competitionEvents(state: ISportsState, getters: any, rootState: IState, rootGetters: RelativeRootGetters): OfferEvent[] {
            return RawFunctions.competitionEvents(rootState, rootGetters);
        },

        promotionalEventGroups(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            return RawFunctions.filteredPromotionalEvents(state, rootState, rootGetters);
        },

        topTenEvents(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            const { type } = getters.selectedItem;
            if (getters.selectedFeaturedItem || (type !== PredefinedItems.all && type !== PredefinedItems.promotions)) {
                return [];
            }

            return RawFunctions.topTenEvents(state, rootState, rootGetters);
        },

        superLiveEvents(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            return RawFunctions.getSuperLiveEvents(state, rootState, rootGetters);
        },

        pathName(state: ISportsState, getters: any, rootState: IState, rootGetters: any): string {
            return rootGetters['navigation/route'].path;
        },

        sportsFilter(state: ISportsState, getters: any, rootState: IState, rootGetters: any) {
            return RawFunctions.sportsFilter(state, rootState, rootGetters);
        },

        selectedTreeItems: RawFunctions.selectedTreeItems,

        selectedFeaturedItem: RawFunctions.selectedFeaturedItem,

        isLoading: createSimpleGetter<ISportsState, keyof ISportsState>('isLoading'),

        timeFilter(state: ISportsState, getters: any, rootState: IState) {
            return RawFunctions.getTimeFilter(rootState);
        },
    },
    actions: {
        setSelectedSportSidebarItem: (
            context: ActionContext<ISportsState, IState>,
            { selectedTreeItems }: { selectedTreeItems: any[] },
        ) => {
            context.dispatch('setSelectedTreeItem', selectedTreeItems);

            AnalyticsSDK.getInstance().log(
                'Sport_Menu_Click',
                getSportMenuData(selectedTreeItems) as any,
            );
        },
        /**
         * When someone clicks on item in tree this action sets selectedItem objects
         * and updates router's URL with new path
         *
         * Example: we click on football > anglia this action sets url to offer/football/anglia
         */
        setSelectedTreeItem: (
            { commit, dispatch, rootGetters }:
                { commit: Commit; dispatch: Dispatch; rootGetters: any },
            selectedTreeItems: any[],
        ) => {
            commit(SET_SELECTED_TREE_ITEMS, selectedTreeItems);

            const pathParts = getSelectedTreeItemSlugs(selectedTreeItems);

            if (selectedTreeItems[0]?.id === SPORTS_HOME_ID) {
                dispatch(
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    'navigation/push',
                    {
                        name: RouteName.App.Home,
                    },
                    {
                        root: true,
                    },
                );
                return;
            }

            if (rootGetters['navigation/isOnCompetitionsPerSportPage']
                || rootGetters['navigation/isOnSuperAdvantagePerSportPage']
                || rootGetters['navigation/isOnPriceBoostPerSportPage']
                || rootGetters['navigation/isOnSportSocialPage']
            ) {
                dispatch(
                    // eslint-disable-next-line sonarjs/no-duplicate-string
                    'navigation/push',
                    {
                        params: {
                            sportSlug: pathParts[0],
                        },
                    },
                    { root: true });
                return;
            }

            const timeFilter = resolveTimeFilterForExpandedEvent(rootGetters);

            if (timeFilter) {
                pathParts.push(timeFilter);
            }

            const path = [Link.Sport.PrematchOffer, ...pathParts].join('/');
            dispatch('navigation/push', path, { root: true });
        },

        clearSelectedFeaturedItem: ({ commit }: { commit: Commit }) => {
            commit(SET_SELECTED_FEATURED_ITEM, null);
        },
        closeTimeFilter: StoreUtil.createSimpleMutatorAction(CLOSE_TIME_FILTER),

        async loadOffer(context: ActionContext<ISportsState, IState>, sportId?: number) {
            const timeFilterSlug = context.rootState.route.params.timeFilter;

            let timeSpan: TimeSpan;
            if (timeFilterSlug && isValidDate(toDate(timeFilterSlug))) {
                const desiredDate = toDate(timeFilterSlug);
                timeSpan = {
                    startDate: BookieDate.getStartDateForDate(desiredDate),
                    endDate: BookieDate.getEndDateForDate(desiredDate),
                };
            } else {
                const timeFilter = getTimeFilterFromSlug(timeFilterSlug);
                timeSpan = getTimeSpanForTimeFilter(timeFilter);
            }
            context.commit(SET_IS_LOADING, true);
            await context.dispatch('data/sportOffer/fetchEvents', { timeSpan, sportId }, { root: true });
            context.commit(SET_IS_LOADING, false);
        },
    },
};

export default store;
export type SportOfferUIStore = typeof store;

function getSelectedTreeItemSlugs(selectedTreeItems: any[]) {
    const sport = selectedTreeItems[0];

    if (sport && sport.id !== ALL_ID) {
        return selectedTreeItems.map((item: INode) => {
            return slugify(item.urlName);
        });
    }
    return [];
}

function resolveTimeFilterForExpandedEvent(rootGetters: any): string | undefined {
    const isOnSportLivePage = rootGetters['navigation/isOnSportLivePage'] as boolean;

    return isOnSportLivePage ? TimeFilterSlug.Live : TimeFilterSlug.Today;
}
